import { useState, useEffect, useCallback, memo } from 'react';
import { createPortal } from 'react-dom';
import { Av, Bdg, PH } from '../../components/shared';
import { useApp } from '../../context/AppContext';
import * as api from '../../services/api';

const STATUS_LABELS = { 0: 'pending', 1: 'active', 2: 'disconnected' };
const STATUS_FILTERS = [
  { value: '', label: 'All Statuses' },
  { value: '1', label: 'Pending' },
  { value: '2', label: 'Active' },
  { value: '3', label: 'Disconnected' },
];
const SORT_OPTIONS = [
  { value: '', label: 'Newest First' },
  { value: 'created_asc', label: 'Oldest First' },
  { value: 'created_desc', label: 'Newest First' },
];

function formatDate(d) {
  if (!d) return '—';
  const date = new Date(d);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export const PageRequests = memo(() => {
  const { showToast } = useApp();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [totalCount, setTotalCount] = useState(0);
  const [confirmModal, setConfirmModal] = useState(null); // { type: 'approve'|'reject', id, name }
  const [actionLoading, setActionLoading] = useState(null);

  const fetchRequests = useCallback(async (p = page, search = q, st = status, sort = sortBy) => {
    setLoading(true);
    try {
      const res = await api.getRequests(p, { search, status: st, sort_by: sort });
      const paginated = res.data || res;
      const items = paginated.data || paginated;
      setRequests(Array.isArray(items) ? items : []);
      setTotalCount(res.total ?? paginated.total ?? 0);
      setPagination({
        current_page: paginated.current_page || 1,
        last_page: paginated.last_page || 1,
        total: paginated.total || (Array.isArray(items) ? items.length : 0),
      });
    } catch (e) {
      showToast(e.message || 'Failed to load requests', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRequests(page, q, status, sortBy); }, [page]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); fetchRequests(1, q, status, sortBy); }, 400);
    return () => clearTimeout(t);
  }, [q]);

  // Filter/sort changes
  useEffect(() => {
    setPage(1);
    fetchRequests(1, q, status, sortBy);
  }, [status, sortBy]);

  const handleApprove = async (id) => {
    setActionLoading(id);
    try {
      await api.acceptRequest(id);
      showToast('Request approved successfully!');
      setConfirmModal(null);
      fetchRequests(page, q, status, sortBy);
    } catch (e) {
      showToast(e.message || 'Failed to approve request', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (id) => {
    setActionLoading(id);
    try {
      await api.deleteRequest(id);
      showToast('Request rejected successfully!');
      setConfirmModal(null);
      fetchRequests(page, q, status, sortBy);
    } catch (e) {
      showToast(e.message || 'Failed to reject request', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleConfirm = () => {
    if (!confirmModal) return;
    if (confirmModal.type === 'approve') handleApprove(confirmModal.id);
    else handleReject(confirmModal.id);
  };

  // Pagination pages
  const { current_page, last_page, total } = pagination;
  const pages = [];
  for (let i = 1; i <= last_page; i++) {
    if (i === 1 || i === last_page || (i >= current_page - 2 && i <= current_page + 2)) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== '...') {
      pages.push('...');
    }
  }

  const pendingCount = requests.filter(r => r.status === 0).length;

  return (
    <div className="pg fi">
      <PH eyebrow="Team" title="Requests" sub={`${totalCount} total · ${pendingCount} pending`} live />

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          className="inp"
          style={{ maxWidth: 260 }}
          placeholder="Search by name or email…"
          value={q}
          onChange={e => setQ(e.target.value)}
        />
        <select
          className="inp"
          style={{ maxWidth: 170, cursor: 'pointer' }}
          value={status}
          onChange={e => setStatus(e.target.value)}
        >
          {STATUS_FILTERS.map(f => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>
        <select
          className="inp"
          style={{ maxWidth: 170, cursor: 'pointer' }}
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
        >
          {SORT_OPTIONS.map(f => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="card-s">
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--t3)', fontSize: 13 }}>Loading requests...</div>
        ) : requests.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--t3)', fontSize: 13 }}>
            {q || status ? 'No requests match your filters' : 'No requests found'}
          </div>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>User</th>
                <th>Status</th>
                <th>Date</th>
                <th style={{ textAlign: 'right', paddingRight: 14 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.map(r => (
                <tr key={r.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                      {r.photo ? (
                        <img src={r.photo} alt={r.username} style={{ width: 32, height: 32, borderRadius: 10, objectFit: 'cover' }} />
                      ) : (
                        <Av
                          name={(r.username || '??').slice(0, 2).toUpperCase()}
                          grad="linear-gradient(150deg,#64748b,#059669)"
                          size={32}
                        />
                      )}
                      <div>
                        <div style={{ fontWeight: 570, fontSize: 13 }}>{r.username || 'N/A'}</div>
                        <div style={{ fontSize: 11, color: 'var(--t3)' }}>{r.email || 'N/A'}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <Bdg s={STATUS_LABELS[r.status] || 'pending'} />
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--t2)' }}>
                    {formatDate(r.created_at)}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', padding: '0 6px' }}>
                      {r.status === 0 && (<>
                        <button
                          onClick={() => setConfirmModal({ type: 'approve', id: r.id, name: r.username || 'this user' })}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px',
                            borderRadius: 8, border: '1px solid rgba(5,150,105,.22)',
                            background: 'rgba(5,150,105,.07)', cursor: 'pointer',
                            fontSize: 11, fontWeight: 600, color: '#059669',
                            fontFamily: 'var(--f)', transition: 'all .14s', whiteSpace: 'nowrap',
                          }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(5,150,105,.15)'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(5,150,105,.07)'; }}
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => setConfirmModal({ type: 'reject', id: r.id, name: r.username || 'this user' })}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px',
                            borderRadius: 8, border: '1px solid rgba(220,38,38,.2)',
                            background: 'rgba(220,38,38,.07)', cursor: 'pointer',
                            fontSize: 11, fontWeight: 600, color: '#dc2626',
                            fontFamily: 'var(--f)', transition: 'all .14s', whiteSpace: 'nowrap',
                          }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(220,38,38,.15)'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(220,38,38,.07)'; }}
                        >
                          Reject
                        </button>
                      </>)}
                      {r.status === 1 && (
                        <span style={{
                          display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px',
                          borderRadius: 8, background: 'rgba(5,150,105,.08)',
                          fontSize: 11, fontWeight: 600, color: '#059669', whiteSpace: 'nowrap',
                        }}>
                          Approved
                        </span>
                      )}
                      {r.status === 2 && (
                        <span style={{
                          display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px',
                          borderRadius: 8, background: 'rgba(220,38,38,.06)',
                          fontSize: 11, fontWeight: 600, color: '#94a3b8', whiteSpace: 'nowrap',
                        }}>
                          Disconnected
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {last_page > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, padding: '0 4px' }}>
          <div style={{ fontSize: 12, color: 'var(--t3)' }}>
            Showing page {current_page} of {last_page} · {total} requests
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <button
              disabled={current_page <= 1}
              onClick={() => setPage(p => p - 1)}
              style={{
                padding: '6px 12px', borderRadius: 8, border: '1px solid rgba(0,0,0,.1)',
                background: current_page <= 1 ? 'rgba(0,0,0,.03)' : 'rgba(255,255,255,.8)',
                cursor: current_page <= 1 ? 'not-allowed' : 'pointer',
                fontSize: 12, fontWeight: 600, color: current_page <= 1 ? 'var(--t4)' : 'var(--t2)',
                fontFamily: 'var(--f)', transition: 'all .14s',
              }}
            >
              ← Prev
            </button>
            {pages.map((p, i) =>
              p === '...' ? (
                <span key={`dot-${i}`} style={{ padding: '0 4px', color: 'var(--t4)', fontSize: 12 }}>…</span>
              ) : (
                <button key={p} onClick={() => setPage(p)}
                  style={{
                    minWidth: 32, height: 32, borderRadius: 8,
                    border: p === current_page ? '1px solid rgba(100,116,139,.4)' : '1px solid rgba(0,0,0,.08)',
                    background: p === current_page ? 'rgba(100,116,139,.12)' : 'rgba(255,255,255,.8)',
                    cursor: 'pointer', fontSize: 12, fontWeight: p === current_page ? 700 : 500,
                    color: p === current_page ? 'var(--t1)' : 'var(--t2)',
                    fontFamily: 'var(--f)', transition: 'all .14s',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                  onMouseEnter={e => { if (p !== current_page) e.currentTarget.style.background = 'rgba(100,116,139,.08)'; }}
                  onMouseLeave={e => { if (p !== current_page) e.currentTarget.style.background = 'rgba(255,255,255,.8)'; }}
                >
                  {p}
                </button>
              )
            )}
            <button
              disabled={current_page >= last_page}
              onClick={() => setPage(p => p + 1)}
              style={{
                padding: '6px 12px', borderRadius: 8, border: '1px solid rgba(0,0,0,.1)',
                background: current_page >= last_page ? 'rgba(0,0,0,.03)' : 'rgba(255,255,255,.8)',
                cursor: current_page >= last_page ? 'not-allowed' : 'pointer',
                fontSize: 12, fontWeight: 600, color: current_page >= last_page ? 'var(--t4)' : 'var(--t2)',
                fontFamily: 'var(--f)', transition: 'all .14s',
              }}
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      {confirmModal && createPortal(
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(15,23,42,.45)', backdropFilter: 'blur(6px)',
          }}
          onClick={e => { if (e.target === e.currentTarget) setConfirmModal(null); }}
        >
          <div style={{
            width: '100%', maxWidth: 420,
            background: 'rgba(255,255,255,.97)', backdropFilter: 'blur(40px)',
            borderRadius: 20, boxShadow: '0 32px 80px rgba(0,0,0,.18)',
            padding: '28px 28px 24px', animation: 'fadeIn .2s ease',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
              <div style={{
                width: 44, height: 44, borderRadius: 12,
                background: confirmModal.type === 'approve' ? 'rgba(5,150,105,.1)' : 'rgba(220,38,38,.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 22, flexShrink: 0,
              }}>
                {confirmModal.type === 'approve' ? '✓' : '✕'}
              </div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 720, color: '#0f172a' }}>
                  {confirmModal.type === 'approve' ? 'Approve Request?' : 'Reject Request?'}
                </div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                  {confirmModal.type === 'approve'
                    ? `This will approve "${confirmModal.name}" and link them to your enterprise.`
                    : `This will reject the request from "${confirmModal.name}".`
                  }
                </div>
              </div>
            </div>

            <div style={{
              padding: '12px 14px',
              background: confirmModal.type === 'approve' ? 'rgba(5,150,105,.06)' : 'rgba(220,38,38,.06)',
              border: `1px solid ${confirmModal.type === 'approve' ? 'rgba(5,150,105,.15)' : 'rgba(220,38,38,.15)'}`,
              borderRadius: 10, marginBottom: 18,
            }}>
              <div style={{
                fontSize: 12, fontWeight: 600,
                color: confirmModal.type === 'approve' ? '#059669' : '#dc2626',
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <span>{confirmModal.type === 'approve' ? '✓' : '⛔'}</span>
                {confirmModal.type === 'approve' ? 'User will be linked to your enterprise' : 'This action cannot be undone'}
              </div>
              <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 4 }}>
                {confirmModal.type === 'approve'
                  ? "The user's profile will be linked to your enterprise with full access to enterprise features."
                  : 'The request will be permanently removed and the user will need to submit a new request.'
                }
              </div>
            </div>

            <div style={{ display: 'flex', gap: 9, justifyContent: 'flex-end' }}>
              <button
                className="btn bs"
                style={{ padding: '9px 20px', fontSize: 13 }}
                onClick={() => setConfirmModal(null)}
                disabled={!!actionLoading}
              >
                Cancel
              </button>
              <button
                style={{
                  padding: '9px 20px', borderRadius: 10, border: 'none',
                  background: confirmModal.type === 'approve' ? '#059669' : '#dc2626',
                  color: '#fff', cursor: actionLoading ? 'wait' : 'pointer',
                  fontSize: 13, fontWeight: 650, fontFamily: 'var(--f)',
                  transition: 'all .15s',
                  boxShadow: `0 2px 8px ${confirmModal.type === 'approve' ? 'rgba(5,150,105,.3)' : 'rgba(220,38,38,.3)'}`,
                  opacity: actionLoading ? 0.7 : 1,
                }}
                onMouseEnter={e => { if (!actionLoading) e.currentTarget.style.opacity = '.9'; }}
                onMouseLeave={e => { if (!actionLoading) e.currentTarget.style.opacity = '1'; }}
                onClick={handleConfirm}
                disabled={!!actionLoading}
              >
                {actionLoading
                  ? 'Processing...'
                  : confirmModal.type === 'approve' ? 'Approve' : 'Reject'
                }
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
});
